import React, { useEffect, useRef, useState } from "react";

// --- 1. KOMPONEN KONEKTOR SVG (Garis Berjalan) ---
const Connector: React.FC<{ type: "left" | "right" }> = ({ type }) => {
  const isRight = type === "right";

  return (
    // SETTING GARIS:
    // z-0: Layer paling bawah.
    // w-[60%]: Lebar masuk ke area card.
    <div
      className={`absolute top-1/2 w-[60%] h-[110%] z-0 pointer-events-none hidden md:block ${isRight ? "right-[-50%]" : "left-[-50%]"}`}
    >
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        style={{ overflow: "visible" }}
      >
        <path
          d={
            isRight
              ? "M 0,5 H 90 Q 100,5 100,15 V 100"
              : "M 100,5 H 10 Q 0,5 0,15 V 100"
          }
          fill="none"
          stroke="white"
          strokeWidth="3"
          strokeDasharray="8 8"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
          className="animate-walking-line"
        />
      </svg>
    </div>
  );
};

// --- 2. KOMPONEN ANIMASI SCROLL (Reveal Effect) ---
interface RevealProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}

const RevealOnScroll: React.FC<RevealProps> = ({
  children,
  className = "",
  delay = 0,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.2 },
    );

    if (ref.current) observer.observe(ref.current);
    return () => {
      if (ref.current) observer.unobserve(ref.current);
    };
  }, []);

  return (
    <div
      ref={ref}
      className={`${className} transition-all duration-1000 ease-out transform ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

// --- 3. KOMPONEN UTAMA HALAMAN ---
const FluenesiaLearningPath: React.FC = () => {
  return (
    <>
      <style>{`
        @keyframes walking-dash {
          to { stroke-dashoffset: -24; }
        }
        .animate-walking-line {
          animation: walking-dash 1s linear infinite;
        }
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;600;700&display=swap');
      `}</style>

      {/* PERBAIKAN BACKGROUND:
          1. bg-gradient-to-b from-[#FF0009] to-[#990005]: Membuat efek gradasi merah.
          2. Hapus 'min-h-screen': Agar tinggi background pas dengan konten (tidak kepanjangan).
          3. py-8: Mengurangi padding atas bawah (sebelumnya py-10).
      */}
      <div className="bg-gradient-to-b from-[#FF0009] to-[#990005] py-8 overflow-x-hidden font-[Poppins]">
        <div className="text-center text-white mb-10 px-4">
          <RevealOnScroll>
            <h1 className="text-3xl md:text-4xl font-bold leading-tight">
              See How Easy It Is to Learn
              <br />
              with Fluenesia
            </h1>
          </RevealOnScroll>
        </div>

        <div className="container mx-auto px-4 max-w-5xl relative pb-8">
          {/* ================= STEP 1: Trial Class ================= */}
          <div className="relative flex flex-col md:flex-row items-center mb-0 md:mb-0">
            <div className="relative w-full md:w-[45%]">
              <Connector type="right" />

              <RevealOnScroll className="bg-white p-6 rounded-[30px] shadow-lg relative z-30">
                <div className="absolute top-1/2 -translate-y-1/2 -left-8 bg-white w-16 h-16 rounded-full shadow-md flex items-center justify-center border border-gray-100 overflow-hidden">
                  <img
                    src="/icon-trialclass.png"
                    alt="Trial Class"
                    className="w-10 h-10 object-contain"
                  />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2 ml-6">
                  Level Assessment
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed ml-6">
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed
                  do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                </p>
              </RevealOnScroll>
            </div>
            <div className="w-full md:w-[55%]"></div>
          </div>

          {/* ================= STEP 2: Level Assessment ================= */}
          <div className="relative flex flex-col md:flex-row items-center mt-6 md:-mt-12">
            <div className="w-full md:w-[55%]"></div>

            <div className="relative w-full md:w-[45%]">
              <Connector type="left" />

              <RevealOnScroll
                className="bg-white p-6 rounded-[30px] shadow-lg relative z-30"
                delay={200}
              >
                <div className="absolute top-1/2 -translate-y-1/2 -right-8 bg-white w-16 h-16 rounded-full shadow-md flex items-center justify-center border border-gray-100 overflow-hidden">
                  <img
                    src="/icon-assessment.png"
                    alt="Level Assessment"
                    className="w-10 h-10 object-contain"
                  />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2 mr-6">
                  Live Interactive Class
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mr-6">
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed
                  do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                </p>
              </RevealOnScroll>
            </div>
          </div>

          {/* ================= STEP 3: Live Interactive Class ================= */}
          <div className="relative flex flex-col md:flex-row items-center mt-6 md:-mt-12">
            <div className="relative w-full md:w-[45%]">
              <Connector type="right" />

              <RevealOnScroll className="bg-white p-6 rounded-[30px] shadow-lg relative z-30">
                <div className="absolute top-1/2 -translate-y-1/2 -left-8 bg-white w-16 h-16 rounded-full shadow-md flex items-center justify-center border border-gray-100 overflow-hidden">
                  <img
                    src="/icon-live.png"
                    alt="Live Class"
                    className="w-10 h-10 object-contain"
                  />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2 ml-6">
                  Practice & Feedback
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed ml-6">
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed
                  do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                </p>
              </RevealOnScroll>
            </div>
            <div className="w-full md:w-[55%]"></div>
          </div>

          {/* ================= STEP 4: Practice & Feedback ================= */}
          <div className="relative flex flex-col md:flex-row items-center mt-6 md:-mt-12">
            <div className="w-full md:w-[55%]"></div>
            <div className="relative w-full md:w-[45%]">
              <RevealOnScroll
                className="bg-white p-6 rounded-[30px] shadow-lg relative z-30"
                delay={200}
              >
                <div className="absolute top-1/2 -translate-y-1/2 -right-8 bg-white w-16 h-16 rounded-full shadow-md flex items-center justify-center border border-gray-100 overflow-hidden">
                  <img
                    src="/icon-feedback.png"
                    alt="Practice"
                    className="w-10 h-10 object-contain"
                  />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2 mr-6">
                  Speak Indonesian Confidently
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mr-6">
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed
                  do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                </p>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FluenesiaLearningPath;
